import { useMemo } from "react";

import { DateTime } from "luxon";
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BoltIcon,
  ChartBarSquareIcon,
  ClockIcon,
  CubeIcon,
  ScaleIcon,
  SparklesIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import LoadingWheel from "../../shared/LoadingWheel";
import classNames from "../../../utils/classNames";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import useVehicleStockIntelligence from "../../../data/useVehicleStockIntelligence";
import { buildMockVehicleStockIntelligence } from "./intelligenceMockData";
import { IPart, IVehicleStockIntelligence } from "../../../models/Part";

const num = (n: number | null | undefined, fallback = "—") =>
  typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString(undefined, { maximumFractionDigits: 1 })
    : fallback;

const formatDays = (n: number | null | undefined) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n < 1) return "<1 day";
  if (n < 30) return `${Math.round(n)} days`;
  if (n < 365) {
    const months = n / 30;
    return `${months.toFixed(1)} mo`;
  }
  return `${(n / 365).toFixed(1)} yr`;
};

export default function StockVehicleIntelligence({ part }: { part: IPart }) {
  const { intelligence } = useVehicleStockIntelligence(part.id);
  const realData = intelligence.data?.data ?? null;

  // Until the backend ships the endpoint, fall back to deterministic
  // mock data so the tab can be reviewed end-to-end. The `isDemo`
  // flag drives a clear badge in the hero so it's never mistaken for
  // production data.
  const isDemo = !realData;
  const data = useMemo<IVehicleStockIntelligence | null>(
    () => realData ?? buildMockVehicleStockIntelligence(part),
    [realData, part]
  );

  // Vehicle context — fall back to whatever's already on `part.vehicle`
  // so the hero never looks empty even before the bundle loads.
  const vehicle = useMemo(() => {
    const v = part.vehicle;
    return {
      year:
        data?.vehicle?.year ??
        v?.modelYear ??
        v?.dvlaYearOfManufacture ??
        null,
      make: data?.vehicle?.make ?? v?.combinedMake ?? v?.dvlaMake ?? null,
      model:
        data?.vehicle?.model ??
        v?.combinedModel ??
        v?.dvlaModel ??
        null,
      mark: data?.vehicle?.mark ?? v?.mark ?? null,
    };
  }, [data?.vehicle, part.vehicle]);

  const vehicleLabel =
    [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ") ||
    "Unknown vehicle";

  // Mock data is always available, so the panel always has something
  // to show. We only briefly show the loading wheel when the real
  // request is in flight AND we don't yet have anything to display.
  const isLoading = intelligence.isLoading && !data;
  const hasData = !!data;

  return (
    <div className="flex flex-col gap-4">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-lg border border-sky-200 bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50 p-4">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-sky-200/40 to-emerald-200/40 blur-2xl"
        />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 via-cyan-500 to-emerald-500 text-white shadow-sm">
              <ChartBarSquareIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900">
                  Vehicle Stock Intelligence
                </h2>
                {isDemo ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-inset ring-amber-200">
                    Demo data
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200">
                    <SparklesIcon className="h-3 w-3" aria-hidden="true" />
                    Live
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600">
                Supply &amp; demand summary for{" "}
                <span className="font-semibold text-gray-800">
                  {vehicleLabel}
                </span>
                {vehicle.mark != null && (
                  <span className="text-gray-500"> · Mark {vehicle.mark}</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {data?.generatedAt && (
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="h-3 w-3" aria-hidden="true" />
                Updated{" "}
                {formatDateTime(
                  data.generatedAt,
                  DateTime.DATETIME_SHORT,
                  "—"
                )}
              </span>
            )}
            <button
              type="button"
              disabled={intelligence.isFetching}
              onClick={() => intelligence.refetch()}
              className="inline-flex items-center gap-1 rounded-md bg-white/80 px-2 py-1 text-[11px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200 hover:bg-white disabled:opacity-50"
            >
              <ArrowPathIcon
                className={classNames(
                  "h-3 w-3",
                  intelligence.isFetching && "animate-spin"
                )}
                aria-hidden="true"
              />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {isLoading && (
        <Card bodyClassName="p-8 text-center">
          <LoadingWheel />
        </Card>
      )}

      {!isLoading && hasData && (
        <>
          {/* Stock profile */}
          <Card
            title="Stock Profile"
            bodyClassName="p-4"
            headerRight={
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200">
                <CubeIcon className="h-3 w-3" aria-hidden="true" />
                Inventory
              </span>
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <KpiTile
                icon={ArchiveBoxIcon}
                label="Parts in stock"
                sublabel={`for ${[vehicle.year, vehicle.make, vehicle.model]
                  .filter(Boolean)
                  .join(" ") || "this vehicle"}`}
                value={num(data!.partsInStockForYmm)}
                tone="sky"
              />
              <KpiTile
                icon={CubeIcon}
                label={`Same ${part.category?.name ?? "part"} in stock`}
                sublabel={`duplicates of this part type for the same Y/M/M`}
                value={num(data!.sameCategoryInStock)}
                tone={
                  (data!.sameCategoryInStock ?? 0) > 3 ? "amber" : "emerald"
                }
                hint={
                  (data!.sameCategoryInStock ?? 0) > 3
                    ? "Lots of duplicates — consider repricing to move stock."
                    : null
                }
              />
            </div>
          </Card>

          {/* Demand */}
          <Card
            title="Demand"
            bodyClassName="p-4"
            headerRight={
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                <ArrowTrendingUpIcon className="h-3 w-3" aria-hidden="true" />
                Sales velocity
              </span>
            }
          >
            <div className="grid grid-cols-3 gap-3">
              <SalesTile
                label="Sold (30d)"
                value={data!.salesLast30}
                tone="emerald"
              />
              <SalesTile
                label="Sold (60d)"
                value={data!.salesLast60}
                tone="sky"
              />
              <SalesTile
                label="Sold (90d)"
                value={data!.salesLast90}
                tone="indigo"
              />
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <KpiTile
                icon={ScaleIcon}
                label="Avg parts sold / vehicle"
                sublabel="historic average across processed vehicles"
                value={num(data!.averagePartsSoldPerVehicle)}
                tone="indigo"
              />
              <KpiTile
                icon={BanknotesIcon}
                label="Avg revenue / vehicle"
                sublabel="historic average parts revenue per vehicle"
                value={
                  data!.averageRevenuePerVehicle != null
                    ? PriceFormat(data!.averageRevenuePerVehicle)
                    : "—"
                }
                tone="emerald"
              />
            </div>
            <p className="mt-2 text-[11px] text-gray-400">
              Sales counts and averages are scoped to the vehicle's year,
              make, model
              {vehicle.mark != null ? " and mark" : ""}.
            </p>
          </Card>

          {/* ROI */}
          <Card
            title="Return on Investment"
            bodyClassName="p-4"
            headerRight={
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-inset ring-violet-200">
                <BoltIcon className="h-3 w-3" aria-hidden="true" />
                Profitability
              </span>
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <KpiTile
                icon={TruckIcon}
                label="Avg vehicle purchase price"
                sublabel="acquisition cost benchmark for this Y/M/M"
                value={
                  data!.averageVehiclePurchasePrice != null
                    ? PriceFormat(data!.averageVehiclePurchasePrice)
                    : "—"
                }
                tone="amber"
              />
              <KpiTile
                icon={ClockIcon}
                label="Estimated time to break even"
                sublabel="purchase price ÷ comparable sales rate"
                value={formatDays(data!.estimatedDaysToBreakEven)}
                tone={breakEvenTone(data!.estimatedDaysToBreakEven)}
                hint={breakEvenHint(
                  data!.estimatedDaysToBreakEven,
                  data!.averageRevenuePerVehicle,
                  data!.averageVehiclePurchasePrice
                )}
                trailingIcon={breakEvenTrendIcon(
                  data!.averageRevenuePerVehicle,
                  data!.averageVehiclePurchasePrice
                )}
              />
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

type Tone = "sky" | "emerald" | "indigo" | "amber" | "violet" | "red";

const toneClasses: Record<
  Tone,
  { ring: string; bg: string; text: string; iconBg: string; iconText: string }
> = {
  sky: {
    ring: "ring-sky-200",
    bg: "bg-sky-50/60",
    text: "text-sky-700",
    iconBg: "bg-sky-100",
    iconText: "text-sky-600",
  },
  emerald: {
    ring: "ring-emerald-200",
    bg: "bg-emerald-50/60",
    text: "text-emerald-700",
    iconBg: "bg-emerald-100",
    iconText: "text-emerald-600",
  },
  indigo: {
    ring: "ring-indigo-200",
    bg: "bg-indigo-50/60",
    text: "text-indigo-700",
    iconBg: "bg-indigo-100",
    iconText: "text-indigo-600",
  },
  amber: {
    ring: "ring-amber-200",
    bg: "bg-amber-50/60",
    text: "text-amber-700",
    iconBg: "bg-amber-100",
    iconText: "text-amber-600",
  },
  violet: {
    ring: "ring-violet-200",
    bg: "bg-violet-50/60",
    text: "text-violet-700",
    iconBg: "bg-violet-100",
    iconText: "text-violet-600",
  },
  red: {
    ring: "ring-red-200",
    bg: "bg-red-50/60",
    text: "text-red-700",
    iconBg: "bg-red-100",
    iconText: "text-red-600",
  },
};

function KpiTile({
  icon: Icon,
  label,
  sublabel,
  value,
  tone,
  hint,
  trailingIcon: TrailingIcon,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  sublabel?: string;
  value: string;
  tone: Tone;
  hint?: string | null;
  trailingIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>> | null;
}) {
  const t = toneClasses[tone];
  return (
    <div
      className={classNames(
        "rounded-lg border border-gray-200 p-3 ring-1 ring-inset",
        t.bg,
        t.ring
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={classNames(
            "flex h-8 w-8 flex-none items-center justify-center rounded-md",
            t.iconBg,
            t.iconText
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {label}
          </p>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <p className={classNames("text-2xl font-semibold", t.text)}>
              {value}
            </p>
            {TrailingIcon && (
              <TrailingIcon
                className={classNames("h-4 w-4", t.text)}
                aria-hidden="true"
              />
            )}
          </div>
          {sublabel && (
            <p className="mt-0.5 text-[11px] text-gray-500">{sublabel}</p>
          )}
          {hint && (
            <p className="mt-1 text-[11px] font-medium text-gray-600">
              {hint}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SalesTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null | undefined;
  tone: Tone;
}) {
  const t = toneClasses[tone];
  return (
    <div
      className={classNames(
        "rounded-md border border-gray-200 p-3 text-center ring-1 ring-inset",
        t.bg,
        t.ring
      )}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className={classNames("mt-0.5 text-2xl font-semibold", t.text)}>
        {num(value)}
      </div>
    </div>
  );
}

function breakEvenTone(days: number | null | undefined): Tone {
  if (typeof days !== "number" || !Number.isFinite(days)) return "indigo";
  if (days <= 90) return "emerald";
  if (days <= 180) return "sky";
  if (days <= 365) return "amber";
  return "red";
}

function breakEvenHint(
  days: number | null | undefined,
  revenuePerVehicle: number | null | undefined,
  purchasePrice: number | null | undefined
): string | null {
  if (typeof days !== "number" || !Number.isFinite(days)) return null;
  if (revenuePerVehicle != null && purchasePrice != null) {
    if (revenuePerVehicle >= purchasePrice) {
      return "Comparable vehicles already cover their purchase cost on average.";
    }
    const gap = purchasePrice - revenuePerVehicle;
    return `Avg shortfall of ${PriceFormat(gap)} vs purchase price.`;
  }
  return null;
}

function breakEvenTrendIcon(
  revenuePerVehicle: number | null | undefined,
  purchasePrice: number | null | undefined
):
  | React.ComponentType<React.SVGProps<SVGSVGElement>>
  | null {
  if (revenuePerVehicle == null || purchasePrice == null) return null;
  return revenuePerVehicle >= purchasePrice
    ? ArrowTrendingUpIcon
    : ArrowTrendingDownIcon;
}

// Re-export so the type is easy to import alongside the component.
export type { IVehicleStockIntelligence };
